import { useEffect, useState } from "react";

// Colors ECharts reads at option-build time. CSS vars can't drive
// canvas-rendered chart text directly, so the chart widgets resolve
// them once per theme change and embed the resolved hex in the
// ECharts option object (legend.textStyle.color, axisLabel.color,
// tooltip.textStyle.color, tooltip formatter HTML).
//
// Defaults mirror the SCSS fallbacks in
// `palette-style.ts:DEFAULT_PALETTE` — unrethemed dashboards stay
// byte-identical to the pre-picker build, even on a chart that the
// hook hosted.
export interface ChartThemeColors {
  primary: string;
  secondary: string;
}

const DEFAULT_CHART_THEME: ChartThemeColors = {
  primary: "#e6f1f2", // mirrors colors.$white (--color-cp-widget-primary fallback)
  secondary: "#697477", // mirrors colors.$inactiveGrey (--color-cp-widget-secondary fallback)
};

/** The picker-deposited variables live on the central-panel root via
 *  the inline `style` prop set in `dashboards-page.tsx` from
 *  `paletteColorsToStyle`. Charts descend from this, so we look for
 *  the closest ancestor that carries these vars — first a
 *  dedicated `data-theme-anchor` (preferred), then any element that
 *  defines at least one of the project CSS vars, then the
 *  documentElement as the absolute fallback. */
const findThemeAnchor = (): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  const explicit = document.querySelector<HTMLElement>("[data-theme-anchor]");
  if (explicit) return explicit;
  return document.documentElement;
};

const readVars = (anchor: HTMLElement): ChartThemeColors => {
  const style = getComputedStyle(anchor);
  const primary = style.getPropertyValue("--color-cp-widget-primary").trim();
  const secondary = style
    .getPropertyValue("--color-cp-widget-secondary")
    .trim();
  return {
    primary: primary || DEFAULT_CHART_THEME.primary,
    secondary: secondary || DEFAULT_CHART_THEME.secondary,
  };
};

/** Watch the central-panel root's `style` attribute and recompute
 *  the resolved hex whenever it changes. Used by the chart widgets
 *  (trend-line + multi-line) to rebuild their ECharts options
 *  with up-to-date colors as the picker drag-stops land. Falls back
 *  to the project defaults when the vars are unset, so a chart on
 *  a page that never mounted the picker still paints in the
 *  pre-theming baseline. The MutationObserver only fires on real
 *  attribute changes, so this hook doesn't add polling cost. */
export const useThemeColorsForCharts = (
  /** True inside a picker tile. A tile is app chrome with its own fixed dark
   *  background, so the dashboard palette must not tint it: a theme whose
   *  widget text is dark would paint the chart black on the tile. */
  preview = false,
): ChartThemeColors => {
  const [colors, setColors] = useState<ChartThemeColors>(() => {
    if (preview) return DEFAULT_CHART_THEME;
    const anchor = findThemeAnchor();
    return anchor ? readVars(anchor) : DEFAULT_CHART_THEME;
  });

  useEffect(() => {
    if (preview) return undefined;
    const anchor = findThemeAnchor();
    if (!anchor) return undefined;

    // Initial sync — first paint might happen before the picker
    // dropped its inline `style`, so re-read once we mount and
    // are sure the DOM has stabilised.
    setColors(readVars(anchor));

    const observer = new MutationObserver(() => {
      setColors(readVars(anchor));
    });
    // Only the `style` attribute carries the picker output.
    observer.observe(anchor, {
      attributeFilter: ["style"],
      attributes: true,
    });
    return () => observer.disconnect();
  }, [preview]);

  return colors;
};
